// src/pages/MySettingsPage.jsx
// "My settings": which devices are trusted for this account, and registering / revoking them.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Form, Input, Table } from "antd";
import axiosInstance from "../api/axiosInstance";
import notify from "../utils/notify";
import {
  CardTitle,
  DetailList,
  ListToolbar,
  PageHeader,
  RowActions,
  SectionCard,
  StatusTag,
  TabBar,
  fmtDateTime,
  tablePagination,
  useFlatPage,
} from "../components/ui";

/** "Chrome on Windows" from a raw user-agent string. */
const describeAgent = (ua = "") => {
  if (!ua) return "Unknown device";
  const browser = /Edg\//.test(ua) ? "Edge" : /OPR\//.test(ua) ? "Opera" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "Browser";
  const os = /Windows/.test(ua) ? "Windows" : /Android/.test(ua) ? "Android" : /iPhone|iPad|iOS/.test(ua) ? "iOS" : /Mac OS X/.test(ua) ? "macOS" : /Linux/.test(ua) ? "Linux" : "an unknown system";
  return `${browser} on ${os}`;
};

const maskHash = (hash) => {
  if (!hash) return "—";
  if (hash.length <= 10) return hash;
  return `${hash.slice(0, 6)}…${hash.slice(-4)}`;
};

const MySettingsPage = () => {
  useFlatPage();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({ current: null, devices: [] });
  const [view, setView] = useState("Trusted"); // Trusted | All
  const [form] = Form.useForm();
  const initialLabelRef = useRef("");

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/devices/summary");
      const current = res.data?.current ?? null;
      const devices = Array.isArray(res.data?.devices) ? res.data.devices : [];
      setData({ current, devices });

      const label = current?.deviceLabel ?? "";
      initialLabelRef.current = label;
      form.setFieldsValue({ deviceLabel: label });
    } catch (err) {
      notify.apiError(err, "Failed to load device settings.");
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const currentTrusted = !!data.current?.isTrusted;
  const trustedCount = useMemo(() => data.devices.filter((d) => d.isTrusted).length, [data.devices]);
  const devicesForTable = useMemo(() => (view === "Trusted" ? data.devices.filter((d) => d.isTrusted) : data.devices), [data.devices, view]);

  const onRegisterThisDevice = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await axiosInstance.post("/devices/register", { deviceLabel: values.deviceLabel || null });
      notify.success(currentTrusted ? "Device name saved." : "This device is now trusted.");
      await fetchDevices();
    } catch (err) {
      if (err?.errorFields) return; // form validation
      notify.apiError(err, "Failed to register device.");
    } finally {
      setSaving(false);
    }
  };

  const onRevoke = async (row) => {
    try {
      setSaving(true);
      await axiosInstance.post(`/devices/${row.id}/revoke`);
      notify.success("Device is no longer trusted.");
      await fetchDevices();
    } catch (err) {
      notify.apiError(err, "Failed to revoke device.");
    } finally {
      setSaving(false);
    }
  };

  const labelValue = Form.useWatch("deviceLabel", form);
  const labelChanged = (labelValue ?? "") !== (initialLabelRef.current ?? "");
  const canSave = !currentTrusted || labelChanged;

  const columns = [
    {
      title: "Device",
      key: "device",
      render: (_, d) => (
        <div className="hms-cell-stack">
          <span className="hms-cell-main">{d.deviceLabel || describeAgent(d.userAgent)}</span>
          <span className="hms-cell-sub" title={d.userAgent}>
            {d.deviceLabel ? describeAgent(d.userAgent) : "No name set"}
          </span>
        </div>
      ),
    },
    { title: "Last seen", dataIndex: "lastSeenUtc", key: "lastSeenUtc", width: 190, render: (v) => (v ? fmtDateTime(v) : "—") },
    {
      title: "Status",
      dataIndex: "isTrusted",
      key: "isTrusted",
      width: 130,
      render: (v) => <StatusTag status={v ? "available" : "cancelled"}>{v ? "Trusted" : "Not trusted"}</StatusTag>,
    },
    {
      title: "",
      key: "action",
      width: 70,
      align: "right",
      render: (_, row) =>
        row.isTrusted ? (
          <RowActions
            actions={[
              {
                key: "revoke",
                label: "Stop trusting this device",
                icon: "close",
                danger: true,
                confirm: "Stop trusting this device? You may be asked to register it again later.",
                okText: "Stop trusting",
                onClick: () => onRevoke(row),
              },
            ]}
          />
        ) : null,
    },
  ];

  return (
    <div className="hms-page my-settings">
      <PageHeader
        actions={
          <Button onClick={fetchDevices} loading={loading} disabled={saving}>
            Refresh
          </Button>
        }
      />

      {!loading && !data.current ? (
        <Alert
          type="warning"
          showIcon
          title="We can't tell which device this is."
          description="If the app and the API are on different domains, make sure cookies and credentials are allowed so the device id can be read."
        />
      ) : (
        <div className="hms-split">
          <SectionCard title="This device">
            <div className="hms-summary" style={{ marginBottom: 12 }}>
              <div>
                <div className="hms-summary-title">{currentTrusted ? "This device is trusted" : "This device is not trusted yet"}</div>
                <div className="hms-summary-sub">
                  {currentTrusted
                    ? "You'll be recognised on future sign-ins from here."
                    : "Trusting it reduces security warnings when you sign in from here."}
                </div>
              </div>
              <StatusTag status={currentTrusted ? "available" : "pending"}>{currentTrusted ? "Trusted" : "Not trusted"}</StatusTag>
            </div>

            <DetailList
              items={[
                { label: "Browser", value: describeAgent(data.current?.userAgent) },
                { label: "Device ID", value: <code>{data.current?.deviceHashMasked || maskHash(data.current?.deviceHash)}</code> },
                { label: "Last seen", value: data.current?.lastSeenUtc ? fmtDateTime(data.current.lastSeenUtc) : "—" },
              ]}
            />

            <Form form={form} layout="vertical" style={{ marginTop: 16 }} disabled={loading || !data.current}>
              <Form.Item label="Device name (optional)" name="deviceLabel" rules={[{ max: 256, message: "Max 256 characters." }]}>
                <Input placeholder='e.g. "Front Desk PC" or "My Laptop"' allowClear />
              </Form.Item>
              <Button type="primary" block onClick={onRegisterThisDevice} loading={saving} disabled={!canSave || !data.current}>
                {currentTrusted ? "Save name" : "Trust this device"}
              </Button>
            </Form>
          </SectionCard>

          <SectionCard flush title={<CardTitle count={data.devices.length}>Your devices</CardTitle>} extra={<ListToolbar onRefresh={fetchDevices} loading={loading} />}>
            <TabBar
              tabs={[
                { key: "Trusted", label: "Trusted", count: trustedCount },
                { key: "All", label: "All", count: data.devices.length },
              ]}
              value={view}
              onChange={setView}
            />
            <Table
              className="hms-table"
              rowKey="id"
              columns={columns}
              dataSource={devicesForTable}
              loading={loading}
              pagination={tablePagination("device", { hideOnSinglePage: true })}
              scroll={{ x: 600 }}
              locale={{ emptyText: <div className="hms-empty">{view === "Trusted" ? "No trusted devices yet" : "No devices found"}</div> }}
            />
          </SectionCard>
        </div>
      )}
    </div>
  );
};

export default MySettingsPage;
